<script setup lang="ts">
  import { computed } from 'vue';
  import { Checkbox, IconRadio } from '@/shared/ui';
  import type { RecordConfigOption } from '../../api/record-config-options.api';

  const RADIO_GROUP_NAME = 'record-config-stages';
  const EMPTY_MESSAGE = 'Nenhuma etapa cadastrada. Cadastre as etapas na aba Configurações.';

  const props = defineProps<{
    hasStages: boolean | null;
    stageIds: number[];
    stages: RecordConfigOption[];
    isLoading: boolean;
  }>();

  const emit = defineEmits<{
    'update:hasStages': [value: boolean];
    'update:stageIds': [value: number[]];
  }>();

  const hasStagesValue = computed(() => (props.hasStages === null ? '' : String(props.hasStages)));

  const showsEmptyMessage = computed(() => !props.isLoading && props.stages.length === 0);

  function selectHasStages(value: string | undefined): void {
    emit('update:hasStages', value === 'true');
  }

  function isChecked(stageId: number): boolean {
    return props.stageIds.includes(stageId);
  }

  function toggle(stageId: number): void {
    const next = isChecked(stageId)
      ? props.stageIds.filter((id) => id !== stageId)
      : props.stages.map(({ id }) => id).filter((id) => id === stageId || isChecked(id));
    emit('update:stageIds', next);
  }
</script>

<template>
  <div class="flex flex-col gap-4 px-8 py-6" data-testid="record-config-stages">
    <span class="text-sm font-semibold text-gray-07">Etapas disponíveis ao Registro:</span>

    <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
      <IconRadio
        :model-value="hasStagesValue"
        value="true"
        label="Informar opções"
        :name="RADIO_GROUP_NAME"
        variant="medium"
        @update:model-value="selectHasStages"
      />
      <IconRadio
        :model-value="hasStagesValue"
        value="false"
        label="Não aplicável"
        :name="RADIO_GROUP_NAME"
        variant="medium"
        @update:model-value="selectHasStages"
      />
    </div>

    <template v-if="hasStages">
      <p
        v-if="showsEmptyMessage"
        class="text-sm text-gray-05"
        data-testid="record-config-stages-empty"
      >
        {{ EMPTY_MESSAGE }}
      </p>
      <div
        v-else
        class="flex flex-wrap items-center gap-x-6 gap-y-3"
        data-testid="record-config-stages-list"
      >
        <label
          v-for="stage in stages"
          :key="stage.id"
          class="flex cursor-pointer items-center gap-2 text-sm whitespace-nowrap text-gray-07"
          data-testid="record-config-stage"
        >
          <Checkbox
            :id="`record-config-stage-${stage.id}`"
            :checked="isChecked(stage.id)"
            @toggle="toggle(stage.id)"
          />
          <span>{{ stage.name }}</span>
        </label>
      </div>
    </template>
  </div>
</template>
